import React, { useState } from 'react';
import { MomentumScore } from '@/api/entities';
import { PersonaActivity } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const MOMENTUM_FIXES = [
  { account_id: 'canada_002_001', weekly_period: '2025-07-18', momentum_score_change: 5 },
  { account_id: 'germany_002_001', weekly_period: '2025-07-18', momentum_score_change: 2 },
  { account_id: 'germany_002_001', weekly_period: '2025-06-13', momentum_score_change: -2 },
  { account_id: 'germany_002_001', weekly_period: '2025-07-25', momentum_score_change: 5 },
  { account_id: 'canada_002_001', weekly_period: '2025-02-14', momentum_score_change: 3 },
  { account_id: 'canada_002_001', weekly_period: '2025-05-30', momentum_score_change: 2 },
  { account_id: 'tunisia_002_001', weekly_period: '2025-01-10', momentum_score_change: 3 },
  { account_id: 'morocco_002_001', weekly_period: '2025-07-18', momentum_score_change: 5 },
  { account_id: 'kenya_002_001', weekly_period: '2025-02-14', momentum_score_change: 5 },
  { account_id: 'southafrica_002_001', weekly_period: '2025-06-13', momentum_score_change: 5 },
  { account_id: 'usa_002_001', weekly_period: '2025-02-07', momentum_score_change: 3 },
];

const PERSONA_FIXES = [
  { person_id: 'bharding', date: '2025-05-27', persona_activity_score_change: 5 },
  { person_id: 'cshepstone', date: '2025-01-15', persona_activity_score_change: 4 },
  { person_id: 'cnzivo', date: '2025-05-01', persona_activity_score_change: 2 },
  { person_id: 'eabdi', date: '2025-06-18', persona_activity_score_change: 2 },
  { person_id: 'eabdi', date: '2025-06-17', persona_activity_score_change: 2 },
  { person_id: 'fbarasa', date: '2025-07-07', persona_activity_score_change: 1 },
  { person_id: 'hkrimmer', date: '2025-03-20', persona_activity_score_change: 2 },
  { person_id: 'jbarber', date: '2025-03-17', persona_activity_score_change: 4 },
  { person_id: 'jmwachi', date: '2025-07-11', persona_activity_score_change: 2 },
  { person_id: 'jmeyer', date: '2025-06-13', persona_activity_score_change: 2 },
  { person_id: 'jwasterlain', date: '2025-06-10', persona_activity_score_change: 4 },
  { person_id: 'kkchir', date: '2025-03-07', persona_activity_score_change: 2 },
  { person_id: 'mntombela', date: '2025-05-22', persona_activity_score_change: 3 },
  { person_id: 'mstampfl', date: '2025-05-30', persona_activity_score_change: 4 },
  { person_id: 'mm', date: '2025-07-17', persona_activity_score_change: 5 },
  { person_id: 'mm', date: '2025-06-05', persona_activity_score_change: 3 },
  { person_id: 'mdonofriojones', date: '2025-06-10', persona_activity_score_change: 2 },
  { person_id: 'mjlok', date: '2025-04-22', persona_activity_score_change: 2 },
  { person_id: 'nbenneji', date: '2025-01-27', persona_activity_score_change: 2 },
  { person_id: 'nbenneji', date: '2025-03-31', persona_activity_score_change: 3 },
  { person_id: 'rzylstra', date: '2025-03-19', persona_activity_score_change: 2 },
  { person_id: 'selmalizi', date: '2025-01-16', persona_activity_score_change: 3 },
  { person_id: 'selmalizi', date: '2025-06-02', persona_activity_score_change: 2 },
  { person_id: 'szakuzanjakazi', date: '2025-06-04', persona_activity_score_change: 2 },
  { person_id: 'skamanguya', date: '2025-07-01', persona_activity_score_change: -4 },
  { person_id: 'tfalk', date: '2025-06-13', persona_activity_score_change: 2 },
  { person_id: 'vandres', date: '2025-05-19', persona_activity_score_change: 2 },
  { person_id: 'ypang', date: '2025-05-28', persona_activity_score_change: 1 },
];

export default function FixDemarqueMomentum() {
  const [momentumProcessing, setMomentumProcessing] = useState(false);
  const [momentumResults, setMomentumResults] = useState([]);
  const [momentumCompleted, setMomentumCompleted] = useState(false);
  const [momentumRecords, setMomentumRecords] = useState([]);
  const [momentumLoading, setMomentumLoading] = useState(false);

  const [personaProcessing, setPersonaProcessing] = useState(false);
  const [personaResults, setPersonaResults] = useState([]);
  const [personaCompleted, setPersonaCompleted] = useState(false);
  const [personaRecords, setPersonaRecords] = useState([]);
  const [personaLoading, setPersonaLoading] = useState(false);

  const loadMomentumRecords = async () => {
    setMomentumLoading(true);
    try {
      const allRecords = await MomentumScore.filter({ tenant_id: 'demarque' });
      console.log('📊 Loaded demarque momentum records:', allRecords);
      setMomentumRecords(allRecords);
    } catch (error) {
      console.error('❌ Error loading momentum records:', error);
    } finally {
      setMomentumLoading(false);
    }
  };

  const loadPersonaRecords = async () => {
    setPersonaLoading(true);
    try {
      const allRecords = await PersonaActivity.filter({ tenant_id: 'demarque' }, '-date_published');
      console.log('📊 Loaded demarque persona activity records:', allRecords);
      setPersonaRecords(allRecords);
    } catch (error) {
      console.error('❌ Error loading persona records:', error);
    } finally {
      setPersonaLoading(false);
    }
  };

  const handleFixMomentum = async () => {
    setMomentumProcessing(true);
    setMomentumResults([]);
    setMomentumCompleted(false);

    try {
      const allRecords = await MomentumScore.filter({ tenant_id: 'demarque' });
      
      console.log('📊 Found demarque momentum records:', {
        count: allRecords.length,
        records: allRecords.map(r => ({
          id: r.id,
          account_id: r.account_id,
          institution_name: r.institution_name,
          weekly_period: r.weekly_period,
          momentum_score_change: r.momentum_score_change
        }))
      });

      const updateResults = [];

      for (const fix of MOMENTUM_FIXES) {
        try {
          const matchingRecords = allRecords.filter(r => r.account_id === fix.account_id);
          
          if (matchingRecords.length === 0) {
            updateResults.push({
              id: fix.account_id,
              status: 'error',
              message: 'No matching record found'
            });
            continue;
          }

          let recordToUpdate = matchingRecords.find(r => {
            const sanitized = typeof r.momentum_score_change === 'string' 
              ? parseFloat(r.momentum_score_change.replace(/–/g, '-').trim())
              : r.momentum_score_change;
            return sanitized === fix.momentum_score_change;
          });

          if (!recordToUpdate && matchingRecords.length === 1) {
            recordToUpdate = matchingRecords[0];
          }

          if (!recordToUpdate) {
            updateResults.push({
              id: fix.account_id,
              status: 'error',
              message: `Found ${matchingRecords.length} records but couldn't match`
            });
            continue;
          }

          await MomentumScore.update(recordToUpdate.id, {
            weekly_period: fix.weekly_period,
            momentum_score_change: fix.momentum_score_change
          });

          updateResults.push({
            id: fix.account_id,
            status: 'success',
            message: `Updated ${recordToUpdate.institution_name}: period → ${fix.weekly_period}, change: ${fix.momentum_score_change}`
          });

          console.log(`✅ Updated momentum for ${fix.account_id}`);
        } catch (error) {
          console.error(`❌ Error updating ${fix.account_id}:`, error);
          updateResults.push({
            id: fix.account_id,
            status: 'error',
            message: error.message
          });
        }
      }

      setMomentumResults(updateResults);
      setMomentumCompleted(true);
      await loadMomentumRecords();
    } catch (error) {
      console.error('❌ Error in momentum fix process:', error);
      setMomentumResults([{ status: 'error', message: 'Failed: ' + error.message }]);
    } finally {
      setMomentumProcessing(false);
    }
  };

  const handleFixPersona = async () => {
    setPersonaProcessing(true);
    setPersonaResults([]);
    setPersonaCompleted(false);

    try {
      const allRecords = await PersonaActivity.filter({ tenant_id: 'demarque' });
      
      console.log('📊 Found demarque persona activity records:', {
        count: allRecords.length,
        records: allRecords.map(r => ({
          id: r.id,
          person_id: r.person_id,
          full_name: r.full_name,
          date: r.date,
          persona_activity_score_change: r.persona_activity_score_change
        }))
      });

      const updateResults = [];

      for (const fix of PERSONA_FIXES) {
        try {
          const matchingRecords = allRecords.filter(r => r.person_id === fix.person_id);
          
          if (matchingRecords.length === 0) {
            updateResults.push({
              id: fix.person_id,
              status: 'error',
              message: 'No matching record found'
            });
            continue;
          }

          let recordToUpdate = matchingRecords.find(r => {
            const sanitized = typeof r.persona_activity_score_change === 'string' 
              ? parseFloat(r.persona_activity_score_change.replace(/–/g, '-').trim())
              : r.persona_activity_score_change;
            return sanitized === fix.persona_activity_score_change;
          });

          if (!recordToUpdate && matchingRecords.length === 1) {
            recordToUpdate = matchingRecords[0];
          }

          if (!recordToUpdate) {
            updateResults.push({
              id: fix.person_id,
              status: 'error',
              message: `Found ${matchingRecords.length} records but couldn't match by score`
            });
            continue;
          }

          await PersonaActivity.update(recordToUpdate.id, {
            date: fix.date,
            persona_activity_score_change: fix.persona_activity_score_change
          });

          updateResults.push({
            id: fix.person_id,
            status: 'success',
            message: `Updated ${recordToUpdate.full_name || fix.person_id}: date → ${fix.date}, change: ${fix.persona_activity_score_change}`
          });

          console.log(`✅ Updated persona activity for ${fix.person_id}`);
        } catch (error) {
          console.error(`❌ Error updating ${fix.person_id}:`, error);
          updateResults.push({
            id: fix.person_id,
            status: 'error',
            message: error.message
          });
        }
      }

      setPersonaResults(updateResults);
      setPersonaCompleted(true);
      await loadPersonaRecords();
    } catch (error) {
      console.error('❌ Error in persona fix process:', error);
      setPersonaResults([{ status: 'error', message: 'Failed: ' + error.message }]);
    } finally {
      setPersonaProcessing(false);
    }
  };

  React.useEffect(() => {
    loadMomentumRecords();
    loadPersonaRecords();
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Fix Demarque Date Fields</h1>
      
      <Tabs defaultValue="momentum" className="w-full">
        <TabsList>
          <TabsTrigger value="momentum">Momentum Scores</TabsTrigger>
          <TabsTrigger value="persona">Persona Activities</TabsTrigger>
        </TabsList>
        
        <TabsContent value="momentum">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Fix Momentum Score Dates</CardTitle>
              <p className="text-sm text-gray-600">
                Updates {MOMENTUM_FIXES.length} momentum records with correct weekly_period dates
              </p>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleFixMomentum} 
                disabled={momentumProcessing || momentumCompleted}
              >
                {momentumProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : momentumCompleted ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Completed
                  </>
                ) : (
                  'Fix Momentum Dates'
                )}
              </Button>

              {momentumResults.length > 0 && (
                <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                  {momentumResults.map((result, idx) => (
                    <div 
                      key={idx}
                      className={`p-2 rounded border text-sm ${
                        result.status === 'success' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {result.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className="font-medium">{result.id}</p>
                          <p className="text-xs">{result.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Current Momentum Records</span>
                <Button variant="outline" size="sm" onClick={loadMomentumRecords} disabled={momentumLoading}>
                  {momentumLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Account ID</th>
                      <th className="text-left py-2 px-2">Institution</th>
                      <th className="text-left py-2 px-2">Weekly Period</th>
                      <th className="text-left py-2 px-2">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {momentumRecords.map(record => (
                      <tr key={record.id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2 font-mono">{record.account_id}</td>
                        <td className="py-2 px-2">{record.institution_name}</td>
                        <td className="py-2 px-2">{record.weekly_period}</td>
                        <td className="py-2 px-2">{record.momentum_score_change}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="persona">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Fix Persona Activity Dates</CardTitle>
              <p className="text-sm text-gray-600">
                Updates {PERSONA_FIXES.length} persona activity records with correct dates
              </p>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleFixPersona} 
                disabled={personaProcessing || personaCompleted}
              >
                {personaProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : personaCompleted ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Completed
                  </>
                ) : (
                  'Fix Persona Dates'
                )}
              </Button>

              {personaResults.length > 0 && (
                <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                  {personaResults.map((result, idx) => (
                    <div 
                      key={idx}
                      className={`p-2 rounded border text-sm ${
                        result.status === 'success' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {result.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className="font-medium">{result.id}</p>
                          <p className="text-xs">{result.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Current Persona Activity Records</span>
                <Button variant="outline" size="sm" onClick={loadPersonaRecords} disabled={personaLoading}>
                  {personaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Person ID</th>
                      <th className="text-left py-2 px-2">Full Name</th>
                      <th className="text-left py-2 px-2">Date</th>
                      <th className="text-left py-2 px-2">Score Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personaRecords.map(record => (
                      <tr key={record.id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2 font-mono">{record.person_id}</td>
                        <td className="py-2 px-2">{record.full_name}</td>
                        <td className="py-2 px-2">{record.date}</td>
                        <td className="py-2 px-2">{record.persona_activity_score_change}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}